/**
 * Review workflow types matching backend schemas
 */

export const REVIEW_PAGE_SIZE_MAX = 100;
export const COMMENT_TYPE_GENERAL = 'general';
export const COMMENT_TYPE_REQUEST_CHANGES = 'request_changes';

export type ReviewStatus = 'in_review' | 'approved' | 'changes_requested' | 'closed';
export type ReviewerStatus = 'pending' | 'approved' | 'changes_requested';
export type ReviewerRole = 'reviewer' | 'approver';
export type CommentStatus = 'open' | 'resolved';
export type CommentType =
  | typeof COMMENT_TYPE_GENERAL
  | typeof COMMENT_TYPE_REQUEST_CHANGES;

export interface Reviewer {
  id: string;
  user_email: string;
  user_name: string;
  role: ReviewerRole;
  status: ReviewerStatus;
  reviewed_at?: string;
  is_owner?: boolean;
}

export interface ReviewerInput {
  email: string;
  role?: ReviewerRole;
}

export interface UserSearchResult {
  username: string;
  email: string;
  display_name?: string;
}

export interface TextSelection {
  start: number;
  end: number;
  text: string;
}

export interface Comment {
  id: string;
  review_id: string;
  reviewer_id: string;
  reviewer_name: string;
  reviewer_role?: ReviewerRole;
  section_id: string;
  comment: string;
  comment_type: CommentType;
  text_selection?: TextSelection;
  parent_id?: string;
  replies?: Comment[];
  status: CommentStatus;
  created_at: string;
  resolved_at?: string;
  resolved_by_name?: string;
}

export interface Review {
  id: string;
  project_id: number;
  project_name: string;
  status: ReviewStatus;
  reviewers: Reviewer[];
  comments: Comment[];
  created_at: string;
  updated_at: string;
}

export interface ReviewSummary {
  id: string;
  review_id?: string | null;
  project_id: number;
  project_name: string;
  status: ReviewStatus;
  reviewer_count: number;
  approver_count: number;
  approver_missing: boolean;
  approved_count: number;
  pending_count: number;
  changes_requested_count: number;
  comment_count: number;
  updated_at: string;
  requires_review_setup?: boolean;
}

export interface ReviewerAssignment {
  id: string;
  project_id: number;
  project_name: string;
  review_status: ReviewStatus;
  my_status: ReviewerStatus;
  my_role: ReviewerRole;
  reviewer_count: number;
  approved_count: number;
  pending_count: number;
  changes_requested_count: number;
  comment_count: number;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  reviews?: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateReviewRequest {
  project_id: number;
  reviewer_emails?: string[];
  reviewers?: ReviewerInput[];
}

export interface AddReviewersRequest {
  reviewer_emails?: string[];
  reviewers?: ReviewerInput[];
}

export interface ReviewStatusUpdate {
  status: ReviewStatus;
}

export interface ReviewerStatusUpdate {
  status: ReviewerStatus;
}

export interface CommentCreate {
  section_id: string;
  comment: string;
  comment_type?: CommentType;
  text_selection?: TextSelection;
  parent_id?: string;
}

export interface CommentStatusUpdate {
  status: CommentStatus;
}

/**
 * Peer Review Checklist types matching backend peer_review_dto schemas.
 */
export type PeerReviewArchieStatus =
  | 'yes'
  | 'no'
  | 'partial'
  | 'not_assessed';

export type PeerReviewDebateRole = 'optimistic' | 'pessimistic' | 'user';

export interface PeerReviewDebateMessage {
  role: PeerReviewDebateRole;
  message: string;
  author?: string | null;
  created_at?: string | null;
  citations?: PeerReviewCitation[] | null;
}

/**
 * One exact guidance citation used in Archie's verdict or recommendation.
 */
export interface PeerReviewCitation {
  document_title: string;
  principle_or_rule: string;
}

/**
 * One architect review comment rendered as a chat bubble in the
 * Architect Review column. Authored by a review participant.
 */
export interface PeerReviewArchitectComment {
  author: string;
  author_display_name?: string | null;
  message: string;
  created_at: string;
  is_archie?: boolean;
}

/**
 * One recommendation point with its supporting guidance citations.
 */
export interface PeerReviewRecommendationEntry {
  text: string;
  citations: PeerReviewCitation[];
}

/**
 * Archie's synthesized recommendation for a single checklist item.
 */
export interface PeerReviewRecommendation {
  summary: string;
  summary_citations: PeerReviewCitation[];
  aligned: PeerReviewRecommendationEntry[];
  unresolved: PeerReviewRecommendationEntry[];
  options: PeerReviewRecommendationEntry[];
}

/**
 * One document section edit Archie applied during a "Let Archie Fix This" run.
 */
export interface PeerReviewFixChange {
  section_name: string;
  section_title: string;
  change_description: string;
  section_anchor?: string;
  citations: PeerReviewCitation[];
  highlighted_text?: string | null;
}

export type PeerReviewFixStatus = 'succeeded' | 'failed' | 'no_changes' | 'undone';

/**
 * Outcome of a "Let Archie Fix This" run for a checklist item.
 */
export interface PeerReviewFixResult {
  // 'undone' = a previously-succeeded fix the user reverted; the run is kept
  // (not deleted) so the fix card stays visible as a record of the decision.
  status: PeerReviewFixStatus;
  summary: string;
  changes: PeerReviewFixChange[];
  sections_changed: string[];
  can_undo?: boolean;
  can_keep?: boolean;
  created_at?: string | null;
  completed_at?: string | null;
}

export interface PeerReviewItem {
  checklist_item_id: number;
  category: string;
  sub_category?: string | null;
  checklist_item: string;
  enterprise_guidance: string[];
  archie_agent_guidance?: string | null;
  sort_order: number;
  is_custom?: boolean;
  peer_review_checked: boolean;
  peer_reviewed_by_name?: string | null;
  peer_reviewed_at?: string | null;
  archie_answer?: string | null;
  archie_citations?: PeerReviewCitation[] | null;
  archie_status?: PeerReviewArchieStatus | null;
  archie_score?: number | null;
  archie_generated_at?: string | null;
  archie_debate?: PeerReviewDebateMessage[] | null;
  archie_recommendation?: PeerReviewRecommendation | null;
  architect_review?: string | null;
  architect_reviewed_by_name?: string | null;
  architect_reviewed_at?: string | null;
  architect_comments?: PeerReviewArchitectComment[] | null;
  archie_fix?: PeerReviewFixResult | null;
}

export interface PeerReviewList {
  review_id: string;
  items: PeerReviewItem[];
  archie_generated: boolean;
}

export interface CustomGuidelineUploadResponse {
  name: string;
  blob_path: string;
  message: string;
  duplicate: boolean;
}

